import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Mail } from "lucide-react";
import api from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export const OwnerPayments = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/payments").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-[#0033FF]" />;
  return (
    <div data-testid="owner-payments">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-sm text-zinc-600">{d.live_mode ? "Live payments" : "Test mode: no real money is moving yet."}</div>
        <a href={d.stripe_dashboard} target="_blank" rel="noreferrer" data-testid="owner-stripe-link"
          className="border border-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
          Open Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-2">Active subscriptions ({d.subscriptions.length})</div>
      <div className="border border-zinc-200 divide-y divide-zinc-100 mb-8" data-testid="owner-subscriptions">
        {d.subscriptions.length === 0 && <div className="p-4 text-sm text-zinc-500">None yet.</div>}
        {d.subscriptions.map((s) => (
          <div key={s.id} className="p-3 flex flex-wrap justify-between gap-2 text-sm">
            <span><span className="font-bold">{s.name}</span> · {s.email}</span>
            <span className="text-zinc-600">{s.plan} · {s.billing_cycle} · renews {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "—"}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-2">Checkouts</div>
      <div className="border border-zinc-200 overflow-x-auto" data-testid="owner-transactions">
        <table className="w-full min-w-[600px] text-sm">
          <thead><tr className="text-left text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-zinc-200"><th className="p-3">When</th><th className="p-3">Who</th><th className="p-3">Plan</th><th className="p-3 text-right">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {d.transactions.length === 0 && <tr><td colSpan={5} className="p-4 text-zinc-500">No checkouts yet.</td></tr>}
            {d.transactions.map((t) => (
              <tr key={t.session_id} className="border-b border-zinc-100">
                <td className="p-3 text-xs">{fmt(t.created_at)}</td><td className="p-3">{t.email}</td><td className="p-3">{t.plan} · {t.billing_cycle}</td>
                <td className="p-3 text-right font-mono">${t.amount}</td>
                <td className="p-3"><span className={`text-[10px] uppercase font-bold tracking-wider ${t.payment_status === "paid" ? "text-emerald-700" : "text-zinc-500"}`}>{t.payment_status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const OwnerSupport = () => {
  const [status, setStatus] = useState("open");
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/owner/support", { params: { status } }).then(({ data }) => setTickets(data.tickets));
  useEffect(() => { setTickets(null); load(); }, [status]); // eslint-disable-line

  const setTicket = async (t, s) => {
    try { await api.post(`/owner/support/${t.id}`, { status: s }); toast.success(s === "resolved" ? "Marked resolved" : "Reopened"); load(); }
    catch { toast.error("Couldn't update"); }
  };
  return (
    <div data-testid="owner-support">
      <div className="inline-flex border border-zinc-300 mb-4">
        {["open", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`owner-support-filter-${s}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${status === s ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>{s}</button>
        ))}
      </div>
      {!tickets ? <Loader2 className="w-5 h-5 animate-spin text-[#0033FF]" /> : (
        <div className="border border-zinc-200 divide-y divide-zinc-200">
          {tickets.length === 0 && <div className="p-6 text-sm text-zinc-500" data-testid="owner-support-empty">Nothing here. Nice.</div>}
          {tickets.map((t) => (
            <div key={t.id} className="p-4" data-testid={`owner-ticket-${t.ref}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button onClick={() => setOpenId(openId === t.id ? null : t.id)} className="text-left">
                  <div className="font-bold">{t.subject} <span className="font-mono text-xs text-zinc-400 ml-1">{t.ref}</span></div>
                  <div className="text-xs text-zinc-500">{t.name} · {t.email} · {fmt(t.created_at)} · via {t.source}</div>
                </button>
                <div className="flex gap-2">
                  <a href={`mailto:${t.email}?subject=${encodeURIComponent(`Re: [${t.ref}] ${t.subject}`)}`} data-testid={`owner-ticket-reply-${t.ref}`}
                    className="border border-zinc-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:border-zinc-900 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Reply</a>
                  <button onClick={() => setTicket(t, t.status === "open" ? "resolved" : "open")} data-testid={`owner-ticket-toggle-${t.ref}`}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${t.status === "open" ? "bg-[#0033FF] text-white hover:bg-zinc-900" : "border border-zinc-300 hover:border-zinc-900"}`}>
                    {t.status === "open" ? "Mark resolved" : "Reopen"}
                  </button>
                </div>
              </div>
              {openId === t.id && (
                <div className="mt-3 space-y-3">
                  <p className="text-sm whitespace-pre-wrap border-l-2 border-[#0033FF] pl-3">{t.message}</p>
                  {t.vizi_transcript && <pre className="text-xs bg-zinc-50 p-3 whitespace-pre-wrap">{t.vizi_transcript}</pre>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OwnerAnnouncement = () => {
  const [f, setF] = useState({ message: "", level: "info", active: false, link_url: "", link_label: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/owner/announcement").then(({ data }) => data.announcement && setF({ ...f, ...data.announcement, link_url: data.announcement.link_url || "", link_label: data.announcement.link_label || "" }));
  }, []); // eslint-disable-line
  const save = async (active) => {
    setBusy(true);
    try {
      await api.put("/owner/announcement", { ...f, active, link_url: f.link_url || null, link_label: f.link_label || null });
      setF({ ...f, active }); toast.success(active ? "Banner is live for signed-in users" : "Banner turned off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#0033FF]";
  return (
    <div className="max-w-2xl" data-testid="owner-announcement">
      <p className="text-sm text-zinc-600 mb-4">Shows a notice at the top of every screen for signed-in leaders. Each new message shows again, even to people who dismissed an older one.</p>
      <div className="space-y-3">
        <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} maxLength={300} rows={3} placeholder="e.g. New: share your Vision Pulse with your whole team in one click." className={inp} data-testid="owner-announcement-message" />
        <div className="grid sm:grid-cols-3 gap-2">
          <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className={inp} data-testid="owner-announcement-level">
            <option value="info">Info (blue)</option><option value="success">Good news (green)</option><option value="warning">Heads-up (amber)</option>
          </select>
          <input value={f.link_label} onChange={(e) => setF({ ...f, link_label: e.target.value })} placeholder="Link text (optional)" className={inp} data-testid="owner-announcement-link-label" />
          <input value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} placeholder="/billing or https://…" className={inp} data-testid="owner-announcement-link-url" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => save(true)} disabled={busy || !f.message.trim()} data-testid="owner-announcement-publish"
            className="bg-[#0033FF] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-40">{f.active ? "Update banner" : "Publish banner"}</button>
          {f.active && <button onClick={() => save(false)} disabled={busy} data-testid="owner-announcement-off" className="border border-zinc-900 px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white">Turn off</button>}
          <span className="text-xs text-zinc-500" data-testid="owner-announcement-status">{f.active ? "Live now" : "Not showing"}</span>
        </div>
      </div>
    </div>
  );
};
